/**
 * The image set deployed by the portable Kubernetes data-plane chart.
 *
 * This is a runtime contract shared by the control plane (which decides
 * whether work is deployable) and the in-cluster agent (which performs the
 * Helm mutation). Keeping the names here prevents those two enforcement
 * points from accepting different release shapes.
 */
export const KUBERNETES_RELEASE_IMAGE_NAMES = ["api", "worker", "agent", "web"] as const;

export type KubernetesReleaseImageName = (typeof KUBERNETES_RELEASE_IMAGE_NAMES)[number];

export type KubernetesReleaseImageContract =
  | { ok: true }
  | {
      ok: false;
      missing: KubernetesReleaseImageName[];
      unexpected: string[];
      invalid: string[];
      detail: string;
    };

/**
 * How a release's images reach the registry an environment's nodes pull from.
 *
 * `direct` copies nothing. `auto-import` has the stack's import job copy the
 * digests a signed release names. `customer-import` leaves the copy, scan and
 * promotion to the customer, so the agent only checks the images are there.
 */
export const ARTIFACT_DELIVERY_MODES = ["direct", "auto-import", "customer-import"] as const;

export type ArtifactDelivery = (typeof ARTIFACT_DELIVERY_MODES)[number];

export function isArtifactDelivery(value: unknown): value is ArtifactDelivery {
  return (ARTIFACT_DELIVERY_MODES as readonly unknown[]).includes(value);
}

/**
 * A release's signed bundle: `<registry>/quovy-releases/byoc@sha256:<hex>`.
 * Digest-pinned so the importer fetches exactly the bytes the publish job signed.
 */
export const BUNDLE_REF_PATTERN =
  /^[a-z0-9](?:[a-z0-9.-]*[a-z0-9])?(?::[0-9]{1,5})?\/quovy-releases\/byoc@sha256:[0-9a-f]{64}$/;

/**
 * How a BYOC environment's cloud substrate comes into existence.
 *
 * This is one versioned, discriminated document rather than a mode column plus
 * provider-specific nullable fields. The control plane can therefore never
 * represent a "Quovy-managed" environment without the AWS account and stable
 * ExternalId that make its trust boundary concrete.
 */
export type ProvisioningConfiguration =
  | { version: 1; kind: "customer_provided" }
  | {
      version: 1;
      kind: "quovy_managed_aws";
      awsAccountId: string;
      externalId: string;
      trustedPrincipalArn: string;
    };

export type ProvisioningConfigurationWire =
  | { version: 1; kind: "customer_provided" }
  | {
      version: 1;
      kind: "quovy_managed_aws";
      aws_account_id: string;
      external_id: string;
      trusted_principal_arn: string;
    };

export const BYOC_AWS_PROVISIONER_ROLE_NAME = "QuovyByocProvisioner";

const AWS_ACCOUNT_ID = /^\d{12}$/;
const BYOC_EXTERNAL_ID = /^qbx_[A-Za-z0-9_-]{32,60}$/;
const AWS_ROLE_ARN = /^arn:aws:iam::\d{12}:role\/[A-Za-z0-9+=,.@_/-]{1,512}$/;

export function parseProvisioningConfiguration(value: unknown): ProvisioningConfiguration | null {
  if (!isRecord(value) || value.version !== 1) return null;
  if (value.kind === "customer_provided") {
    if (!hasExactKeys(value, ["version", "kind"])) return null;
    return { version: 1, kind: "customer_provided" };
  }
  if (
    value.kind !== "quovy_managed_aws" ||
    !hasExactKeys(value, [
      "version",
      "kind",
      "aws_account_id",
      "external_id",
      "trusted_principal_arn",
    ]) ||
    typeof value.aws_account_id !== "string" ||
    !AWS_ACCOUNT_ID.test(value.aws_account_id) ||
    typeof value.external_id !== "string" ||
    !BYOC_EXTERNAL_ID.test(value.external_id) ||
    typeof value.trusted_principal_arn !== "string" ||
    !AWS_ROLE_ARN.test(value.trusted_principal_arn)
  ) {
    return null;
  }
  return {
    version: 1,
    kind: "quovy_managed_aws",
    awsAccountId: value.aws_account_id,
    externalId: value.external_id,
    trustedPrincipalArn: value.trusted_principal_arn,
  };
}

function hasExactKeys(value: Record<string, unknown>, expected: readonly string[]): boolean {
  const actual = Object.keys(value);
  return actual.length === expected.length && expected.every((key) => Object.hasOwn(value, key));
}

export function serializeProvisioningConfiguration(
  configuration: ProvisioningConfiguration,
): ProvisioningConfigurationWire {
  return configuration.kind === "customer_provided"
    ? { version: 1, kind: configuration.kind }
    : {
        version: 1,
        kind: configuration.kind,
        aws_account_id: configuration.awsAccountId,
        external_id: configuration.externalId,
        trusted_principal_arn: configuration.trustedPrincipalArn,
      };
}

/** Non-secret trust metadata shown to the customer cloud administrator. */
export type ManagedAwsTrustBootstrapWire = {
  role_name: typeof BYOC_AWS_PROVISIONER_ROLE_NAME;
  role_arn: string;
  trusted_principal_arn: string;
  external_id: string;
  assume_role_policy: {
    Version: "2012-10-17";
    Statement: [
      {
        Effect: "Allow";
        Principal: { AWS: string };
        Action: ["sts:AssumeRole", "sts:SetSourceIdentity"];
        Condition: {
          StringEquals: { "sts:ExternalId": string; "sts:SourceIdentity": string };
        };
      },
    ];
  };
};

export function managedAwsTrustBootstrap(input: {
  awsAccountId: string;
  externalId: string;
  trustedPrincipalArn: string;
}): ManagedAwsTrustBootstrapWire {
  return {
    role_name: BYOC_AWS_PROVISIONER_ROLE_NAME,
    role_arn: `arn:aws:iam::${input.awsAccountId}:role/${BYOC_AWS_PROVISIONER_ROLE_NAME}`,
    trusted_principal_arn: input.trustedPrincipalArn,
    external_id: input.externalId,
    assume_role_policy: {
      Version: "2012-10-17",
      Statement: [
        {
          Effect: "Allow",
          Principal: { AWS: input.trustedPrincipalArn },
          Action: ["sts:AssumeRole", "sts:SetSourceIdentity"],
          Condition: {
            StringEquals: {
              "sts:ExternalId": input.externalId,
              "sts:SourceIdentity": input.externalId,
            },
          },
        },
      ],
    },
  };
}

export function managedAwsTrustBootstrapFromConfiguration(
  configuration: Extract<ProvisioningConfiguration, { kind: "quovy_managed_aws" }>,
): ManagedAwsTrustBootstrapWire {
  return managedAwsTrustBootstrap({
    awsAccountId: configuration.awsAccountId,
    externalId: configuration.externalId,
    trustedPrincipalArn: configuration.trustedPrincipalArn,
  });
}

/**
 * Customer-approved, non-secret inputs for one managed AWS infrastructure plan.
 *
 * All keys are required, including the empty collection defaults. That keeps
 * the persisted document exact and prevents a missing key from acquiring new
 * meaning when a later runner version changes its defaults.
 */
export type ManagedAwsProvisioningParametersWire = {
  version: 1;
  vpc_cidr: string;
  deletion_protection: boolean;
  eks_api_authorized_cidrs: string[];
  eks_admin_principal_arns: string[];
  public_hosted_zone_ids: string[];
  tags: Record<string, string>;
};

export const PROVISIONING_FAILURE_CODES = [
  "invalid_provisioning_inputs",
  "invalid_apply_report",
  "role_assumption_failed",
  "state_bootstrap_failed",
  "terraform_plan_failed",
  "terraform_apply_failed",
] as const;

export type ProvisioningFailureCode = (typeof PROVISIONING_FAILURE_CODES)[number];

export type ProvisioningFailureWire = {
  version: 1;
  code: ProvisioningFailureCode;
  /** Bounded, redacted operator detail; never raw provider stderr. */
  detail: string;
};

export type ProvisioningPlanEvidenceWire = {
  version: 1;
  stack_digest: string;
  plan_digest: string;
  counts: { add: number; change: number; destroy: number };
  resources: Array<{
    address: string;
    action: "create" | "update" | "delete" | "replace";
  }>;
  evidence: {
    store: "customer_s3";
    bucket: string;
    key: string;
    version_id: string;
    region: string;
  };
};

export type ProvisioningResultWire = {
  version: 1;
  account_id: string;
  region: string;
  stack_name: string;
  eks_cluster_name: string;
  state: {
    bucket: string;
    key: string;
    region: string;
  };
  evidence: {
    bucket: string;
    prefix: string;
    region: string;
  };
};

type ProvisioningRunBaseWire = {
  id: string;
  parameters: ManagedAwsProvisioningParametersWire;
  requested_by: string;
  requested_at: string;
};

export type ProvisioningApprovalWire = {
  decision: "approved" | "rejected";
  plan_digest: string;
  decided_by: string;
  decided_at: string;
  reason: string | null;
};

/**
 * Browser/provisioner read model. Each status carries only evidence that is
 * legal in that state; consumers never receive a nullable grab bag.
 */
export type ProvisioningRunWire =
  | {
      id: string;
      status: "invalid";
      requested_by: string;
      requested_at: string;
    }
  | {
      id: string;
      status: "invalid_input";
      requested_by: string;
      requested_at: string;
      failure: ProvisioningFailureWire & { code: "invalid_provisioning_inputs" };
      finished_at: string;
    }
  | {
      id: string;
      status: "invalid_approved_input";
      requested_by: string;
      requested_at: string;
      plan: ProvisioningPlanEvidenceWire;
      planned_at: string;
      approval: ProvisioningApprovalWire & { decision: "approved" };
      failure: ProvisioningFailureWire & { code: "invalid_provisioning_inputs" };
      finished_at: string;
    }
  | (ProvisioningRunBaseWire & { status: "plan_pending" | "planning" })
  | (ProvisioningRunBaseWire & {
      status: "awaiting_approval";
      plan: ProvisioningPlanEvidenceWire;
      planned_at: string;
    })
  | (ProvisioningRunBaseWire & {
      status: "apply_pending" | "applying";
      plan: ProvisioningPlanEvidenceWire;
      planned_at: string;
      approval: ProvisioningApprovalWire & { decision: "approved" };
    })
  | (ProvisioningRunBaseWire & {
      status: "succeeded";
      plan: ProvisioningPlanEvidenceWire;
      planned_at: string;
      approval: ProvisioningApprovalWire & { decision: "approved" };
      result: ProvisioningResultWire;
      finished_at: string;
    })
  | (ProvisioningRunBaseWire & {
      status: "plan_failed" | "plan_timed_out";
      failure: ProvisioningFailureWire;
      finished_at: string;
    })
  | (ProvisioningRunBaseWire & {
      status: "apply_failed" | "apply_timed_out";
      plan: ProvisioningPlanEvidenceWire;
      planned_at: string;
      approval: ProvisioningApprovalWire & { decision: "approved" };
      failure: ProvisioningFailureWire;
      finished_at: string;
    })
  | (ProvisioningRunBaseWire & {
      status: "cancelled";
      plan: ProvisioningPlanEvidenceWire;
      planned_at: string;
      approval: ProvisioningApprovalWire & { decision: "rejected" };
      finished_at: string;
    });

const IPV4_CIDR = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d|[12]\d|3[0-2])$/;
const AWS_PRINCIPAL_ARN = /^arn:aws:iam::\d{12}:role\/[A-Za-z0-9+=,.@_/-]{1,512}$/;
const ROUTE53_ZONE_ID = /^Z[A-Z0-9]{1,31}$/;
const S3_BUCKET = /^(?!xn--)(?!.*-s3alias$)(?!.*--ol-s3$)[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/;
const SHA256 = /^sha256:[0-9a-f]{64}$/;

function isIpv4Cidr(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const match = IPV4_CIDR.exec(value);
  if (!match) return false;
  return match.slice(1, 5).every((octet) => Number(octet) <= 255);
}

function isAwsVpcCidr(value: unknown): value is string {
  if (!isIpv4Cidr(value)) return false;
  const [address, prefixText] = value.split("/");
  const prefix = Number(prefixText);
  if (!address || prefix < 16 || prefix > 28) return false;
  const numericAddress = address
    .split(".")
    .reduce((total, octet) => total * 256 + Number(octet), 0);
  return numericAddress % 2 ** (32 - prefix) === 0;
}

function isStringArray(
  value: unknown,
  options: { min: number; max: number; item: (candidate: string) => boolean },
): value is string[] {
  return (
    Array.isArray(value) &&
    value.length >= options.min &&
    value.length <= options.max &&
    value.every((candidate) => typeof candidate === "string" && options.item(candidate)) &&
    new Set(value).size === value.length
  );
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return (
    isRecord(value) &&
    Object.keys(value).length <= 40 &&
    Object.entries(value).every(
      ([key, entry]) =>
        key.length >= 1 &&
        key.length <= 128 &&
        !key.toLowerCase().startsWith("aws:") &&
        typeof entry === "string" &&
        entry.length <= 256,
    )
  );
}

export function parseManagedAwsProvisioningParameters(
  value: unknown,
): ManagedAwsProvisioningParametersWire | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "version",
      "vpc_cidr",
      "deletion_protection",
      "eks_api_authorized_cidrs",
      "eks_admin_principal_arns",
      "public_hosted_zone_ids",
      "tags",
    ]) ||
    value.version !== 1 ||
    !isAwsVpcCidr(value.vpc_cidr) ||
    typeof value.deletion_protection !== "boolean" ||
    !isStringArray(value.eks_api_authorized_cidrs, {
      min: 1,
      max: 32,
      item: (candidate) =>
        isIpv4Cidr(candidate) && Number(candidate.slice(candidate.lastIndexOf("/") + 1)) >= 8,
    }) ||
    !isStringArray(value.eks_admin_principal_arns, {
      min: 1,
      max: 32,
      item: (candidate) => AWS_PRINCIPAL_ARN.test(candidate),
    }) ||
    !isStringArray(value.public_hosted_zone_ids, {
      min: 0,
      max: 32,
      item: (candidate) => ROUTE53_ZONE_ID.test(candidate),
    }) ||
    !isStringRecord(value.tags)
  ) {
    return null;
  }
  return value as ManagedAwsProvisioningParametersWire;
}

export function parseProvisioningPlanEvidence(value: unknown): ProvisioningPlanEvidenceWire | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "version",
      "stack_digest",
      "plan_digest",
      "counts",
      "resources",
      "evidence",
    ]) ||
    value.version !== 1 ||
    typeof value.stack_digest !== "string" ||
    !SHA256.test(value.stack_digest) ||
    typeof value.plan_digest !== "string" ||
    !SHA256.test(value.plan_digest) ||
    !isRecord(value.counts) ||
    !hasExactKeys(value.counts, ["add", "change", "destroy"]) ||
    ![value.counts.add, value.counts.change, value.counts.destroy].every(
      (count) =>
        typeof count === "number" && Number.isInteger(count) && count >= 0 && count <= 10_000,
    ) ||
    !Array.isArray(value.resources) ||
    value.resources.length > 1_000 ||
    !value.resources.every(
      (resource) =>
        isRecord(resource) &&
        hasExactKeys(resource, ["address", "action"]) &&
        typeof resource.address === "string" &&
        resource.address.length >= 1 &&
        resource.address.length <= 512 &&
        typeof resource.action === "string" &&
        (["create", "update", "delete", "replace"] as const).includes(
          resource.action as "create" | "update" | "delete" | "replace",
        ),
    ) ||
    !isRecord(value.evidence) ||
    !hasExactKeys(value.evidence, ["store", "bucket", "key", "version_id", "region"]) ||
    value.evidence.store !== "customer_s3" ||
    typeof value.evidence.bucket !== "string" ||
    !S3_BUCKET.test(value.evidence.bucket) ||
    typeof value.evidence.key !== "string" ||
    value.evidence.key.length < 1 ||
    value.evidence.key.length > 1024 ||
    typeof value.evidence.version_id !== "string" ||
    value.evidence.version_id.length < 1 ||
    value.evidence.version_id.length > 1024 ||
    typeof value.evidence.region !== "string" ||
    value.evidence.region.length < 1 ||
    value.evidence.region.length > 64
  ) {
    return null;
  }
  return value as ProvisioningPlanEvidenceWire;
}

export function parseProvisioningFailure(value: unknown): ProvisioningFailureWire | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["version", "code", "detail"]) ||
    value.version !== 1 ||
    !PROVISIONING_FAILURE_CODES.includes(value.code as ProvisioningFailureCode) ||
    typeof value.detail !== "string" ||
    value.detail.length < 1 ||
    value.detail.length > 2_000
  ) {
    return null;
  }
  return value as ProvisioningFailureWire;
}

export function parseProvisioningResult(value: unknown): ProvisioningResultWire | null {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "version",
      "account_id",
      "region",
      "stack_name",
      "eks_cluster_name",
      "state",
      "evidence",
    ]) ||
    value.version !== 1 ||
    typeof value.account_id !== "string" ||
    !AWS_ACCOUNT_ID.test(value.account_id) ||
    typeof value.region !== "string" ||
    value.region.length < 1 ||
    value.region.length > 64 ||
    typeof value.stack_name !== "string" ||
    !/^[a-z][a-z0-9-]{2,31}$/.test(value.stack_name) ||
    typeof value.eks_cluster_name !== "string" ||
    value.eks_cluster_name.length < 1 ||
    value.eks_cluster_name.length > 100 ||
    !isRecord(value.state) ||
    !hasExactKeys(value.state, ["bucket", "key", "region"]) ||
    typeof value.state.bucket !== "string" ||
    !S3_BUCKET.test(value.state.bucket) ||
    typeof value.state.key !== "string" ||
    value.state.key.length < 1 ||
    value.state.key.length > 1024 ||
    typeof value.state.region !== "string" ||
    value.state.region.length < 1 ||
    value.state.region.length > 64 ||
    !isRecord(value.evidence) ||
    !hasExactKeys(value.evidence, ["bucket", "prefix", "region"]) ||
    typeof value.evidence.bucket !== "string" ||
    !S3_BUCKET.test(value.evidence.bucket) ||
    typeof value.evidence.prefix !== "string" ||
    value.evidence.prefix.length < 1 ||
    value.evidence.prefix.length > 1024 ||
    typeof value.evidence.region !== "string" ||
    value.evidence.region.length < 1 ||
    value.evidence.region.length > 64
  ) {
    return null;
  }
  return value as ProvisioningResultWire;
}

/** The two browser-facing origins that make one Quovy application surface. */
export type EndpointConfiguration = {
  webOrigin: string;
  apiOrigin: string;
};

export type EndpointConfigurationWire = {
  web_origin: string;
  api_origin: string;
};

/**
 * How the namespace-scoped agent renders public HTTPS for those origins.
 *
 * The certificate field is a non-secret reference (unversioned Key Vault URI,
 * ACM ARN, or cert-manager issuer name). Credential values never cross this
 * protocol. `cert_manager` is the rehearsal/customer-operated profile;
 * production Azure and AWS profiles reject nip.io and other rehearsal hosts.
 */
export const INGRESS_PROFILES = [
  "azure_keyvault",
  "azure_gateway",
  "aws_alb",
  "cert_manager",
] as const;

export type IngressProfile = (typeof INGRESS_PROFILES)[number];

export type IngressConfiguration = {
  version: 1;
  profile: IngressProfile;
  certificate: string;
};

export type IngressConfigurationWire = {
  version: 1;
  profile: IngressProfile;
  certificate: string;
};

const UNVERSIONED_KEY_VAULT_CERTIFICATE =
  /^https:\/\/[a-z0-9-]+\.vault\.azure\.net\/certificates\/[A-Za-z0-9-]+$/;
const ACM_CERTIFICATE_ARN =
  /^arn:aws:acm:[a-z0-9-]+:\d{12}:certificate\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const CERT_MANAGER_ISSUER_NAME = /^(?=.{1,63}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export function helmIngressProfile(profile: IngressProfile): string {
  return profile.replaceAll("_", "-");
}

export function isProductionIngressProfile(profile: IngressProfile): boolean {
  return profile !== "cert_manager";
}

/** Rehearsal-only public hostnames. Never a customer login URL. */
export function isRehearsalPublicHostname(hostname: string): boolean {
  const host = hostname.toLowerCase();
  return (
    host === "localhost" ||
    host.endsWith(".localhost") ||
    host.endsWith(".nip.io") ||
    host.endsWith(".sslip.io") ||
    host.endsWith(".invalid")
  );
}

export function isCustomerLoginOrigin(value: string): boolean {
  const origin = parseEndpointOrigin(value);
  return origin !== null && !isRehearsalPublicHostname(new URL(origin).hostname);
}

function certificateMatchesProfile(profile: IngressProfile, certificate: string): boolean {
  if (profile === "azure_keyvault" || profile === "azure_gateway") {
    return UNVERSIONED_KEY_VAULT_CERTIFICATE.test(certificate);
  }
  if (profile === "aws_alb") {
    return ACM_CERTIFICATE_ARN.test(certificate);
  }
  return CERT_MANAGER_ISSUER_NAME.test(certificate);
}

export function parseIngressConfiguration(value: unknown): IngressConfiguration | null {
  if (!isRecord(value) || !hasExactKeys(value, ["version", "profile", "certificate"])) {
    return null;
  }
  if (value.version !== 1) return null;
  if (!INGRESS_PROFILES.includes(value.profile as IngressProfile)) return null;
  if (typeof value.certificate !== "string" || value.certificate.length > 2048) return null;
  const profile = value.profile as IngressProfile;
  if (!certificateMatchesProfile(profile, value.certificate)) return null;
  return { version: 1, profile, certificate: value.certificate };
}

export function serializeIngressConfiguration(
  configuration: IngressConfiguration,
): IngressConfigurationWire {
  return {
    version: 1,
    profile: configuration.profile,
    certificate: configuration.certificate,
  };
}

/**
 * Non-secret upstream identity configuration delivered to an in-cluster agent.
 *
 * Credential *references* may cross the control-plane protocol, but credential
 * values never do. The customer bootstrap writes those values into the
 * customer-owned secret store and the data-plane chart references that secret
 * by name.
 */
export type AuthAuthorityConfigurationWire =
  | {
      version: 1;
      kind: "quovy_broker";
      issuer: string;
      environment_id: string;
      external_organization_id: string;
    }
  | {
      version: 1;
      kind: "customer_oidc";
      issuer: string;
      client_id: string;
      audience: string;
      external_organization_id: string;
      client_authentication: {
        method: "client_secret" | "private_key_jwt";
        credential: {
          store: "azure_key_vault" | "aws_secrets_manager";
          reference: string;
        };
      };
    }
  | {
      version: 1;
      kind: "trusted_proxy";
      issuer: string;
      audience: string;
      jwks_uri: string;
      external_organization_id: string;
    };

export type EndpointProbe =
  | { status: "ready"; httpStatus: number }
  | { status: "failed"; reason: "http"; httpStatus: number }
  | { status: "failed"; reason: "dns" | "tls" | "timeout" | "network" };

export type EndpointProbeWire =
  | { status: "ready"; http_status: number }
  | { status: "failed"; reason: "http"; http_status: number }
  | { status: "failed"; reason: "dns" | "tls" | "timeout" | "network" };

export type EndpointObservation = {
  configuration: EndpointConfiguration;
  checkedAt: string;
  web: EndpointProbe;
  api: EndpointProbe;
};

export type EndpointObservationWire = {
  configuration: EndpointConfigurationWire;
  checked_at: string;
  web: EndpointProbeWire;
  api: EndpointProbeWire;
};

export type EndpointStateWire =
  | { status: "not_configured" }
  | { status: "provisioning"; endpoints: EndpointConfigurationWire }
  | { status: "stale"; endpoints: EndpointConfigurationWire; last_reported_at: string }
  | { status: "ready"; endpoints: EndpointConfigurationWire; checked_at: string }
  | {
      status: "degraded";
      endpoints: EndpointConfigurationWire;
      checked_at: string;
      web: EndpointProbeWire;
      api: EndpointProbeWire;
    };

/** The chart-owned Services a customer may verify from inside their cluster. */
export const INTERNAL_SERVICE_COMPONENTS = ["web", "api", "agent"] as const;

export type InternalServiceComponent = (typeof INTERNAL_SERVICE_COMPONENTS)[number];

export type InternalServiceEndpoint = {
  component: InternalServiceComponent;
  serviceName: string;
  port: number;
};

export type InternalServiceEndpointWire = {
  component: InternalServiceComponent;
  service_name: string;
  port: number;
  /** In-cluster Service DNS. This is not a public application origin. */
  url: string;
};

export type InternalEndpointObservation =
  | {
      status: "observed";
      checkedAt: string;
      namespace: string;
      services: InternalServiceEndpoint[];
    }
  | {
      status: "unavailable";
      checkedAt: string;
      namespace: string;
    };

export type InternalEndpointObservationWire =
  | {
      status: "observed";
      checked_at: string;
      namespace: string;
      services: InternalServiceEndpointWire[];
    }
  | {
      status: "unavailable";
      checked_at: string;
      namespace: string;
    };

export type InternalEndpointStateWire =
  | { status: "not_reported" }
  | {
      status: "stale";
      last_reported_at: string;
      observation: InternalEndpointObservationWire;
    }
  | {
      status: "unavailable";
      observation: Extract<InternalEndpointObservationWire, { status: "unavailable" }>;
    }
  | {
      status: "partial";
      observation: Extract<InternalEndpointObservationWire, { status: "observed" }>;
      missing_components: InternalServiceComponent[];
    }
  | {
      status: "ready";
      observation: Extract<InternalEndpointObservationWire, { status: "observed" }>;
    };

/** Four missed 30-second check-ins before the control plane calls evidence stale. */
export const AGENT_HEARTBEAT_STALE_MS = 120_000;

/**
 * Canonicalise an operator-supplied public endpoint origin.
 *
 * The control plane, agent, and console all consume this value. Keeping the
 * parser in their shared contract prevents one surface from accepting a path,
 * non-standard port, IP literal, or credential that Kubernetes Ingress later
 * strips or rejects.
 */
const ENDPOINT_DNS_NAME =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;
const IPV4_LITERAL = /^\d+(?:\.\d+){3}$/;

export function parseEndpointOrigin(value: string): string | null {
  try {
    const url = new URL(value);
    if (
      url.protocol !== "https:" ||
      url.username ||
      url.password ||
      url.port ||
      url.pathname !== "/" ||
      url.search ||
      url.hash ||
      !ENDPOINT_DNS_NAME.test(url.hostname) ||
      IPV4_LITERAL.test(url.hostname)
    ) {
      return null;
    }
    return url.origin;
  } catch {
    return null;
  }
}

export function serializeEndpointConfiguration(
  configuration: EndpointConfiguration,
): EndpointConfigurationWire {
  return {
    web_origin: configuration.webOrigin,
    api_origin: configuration.apiOrigin,
  };
}

export function serializeEndpointProbe(probe: EndpointProbe): EndpointProbeWire {
  if (probe.status === "ready") return { status: probe.status, http_status: probe.httpStatus };
  return probe.reason === "http"
    ? { status: probe.status, reason: probe.reason, http_status: probe.httpStatus }
    : { status: probe.status, reason: probe.reason };
}

export function serializeEndpointObservation(
  observation: EndpointObservation,
): EndpointObservationWire {
  return {
    configuration: serializeEndpointConfiguration(observation.configuration),
    checked_at: observation.checkedAt,
    web: serializeEndpointProbe(observation.web),
    api: serializeEndpointProbe(observation.api),
  };
}

const KUBERNETES_DNS_LABEL = /^(?=.{1,63}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

/** Return the chart fullname shared by one validated `<fullname>-<component>` Service name. */
export function internalServiceNamePrefix(
  serviceName: string,
  component: InternalServiceComponent,
): string | null {
  if (!KUBERNETES_DNS_LABEL.test(serviceName)) return null;
  const suffix = `-${component}`;
  if (!serviceName.endsWith(suffix)) return null;
  const prefix = serviceName.slice(0, -suffix.length);
  return KUBERNETES_DNS_LABEL.test(prefix) ? prefix : null;
}

export function internalServiceUrl(input: {
  namespace: string;
  serviceName: string;
  port: number;
}): string {
  return `http://${input.serviceName}.${input.namespace}.svc:${input.port}`;
}

export function internalServicePortForwardCommand(
  input: { namespace: string; serviceName: string; port: number },
  localPort = input.port,
): string {
  return `kubectl -n ${input.namespace} port-forward service/${input.serviceName} ${localPort}:${input.port}`;
}

export function serializeInternalEndpointObservation(
  observation: Extract<InternalEndpointObservation, { status: "observed" }>,
): Extract<InternalEndpointObservationWire, { status: "observed" }>;
export function serializeInternalEndpointObservation(
  observation: Extract<InternalEndpointObservation, { status: "unavailable" }>,
): Extract<InternalEndpointObservationWire, { status: "unavailable" }>;
export function serializeInternalEndpointObservation(
  observation: InternalEndpointObservation,
): InternalEndpointObservationWire;
export function serializeInternalEndpointObservation(
  observation: InternalEndpointObservation,
): InternalEndpointObservationWire {
  if (observation.status === "unavailable") {
    return {
      status: observation.status,
      checked_at: observation.checkedAt,
      namespace: observation.namespace,
    };
  }
  return {
    status: observation.status,
    checked_at: observation.checkedAt,
    namespace: observation.namespace,
    services: observation.services.map((service) => ({
      component: service.component,
      service_name: service.serviceName,
      port: service.port,
      url: internalServiceUrl({
        namespace: observation.namespace,
        serviceName: service.serviceName,
        port: service.port,
      }),
    })),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseEndpointProbeWire(value: unknown): EndpointProbe | null {
  if (!isRecord(value)) return null;
  const validHttpStatus =
    Number.isInteger(value.http_status) &&
    (value.http_status as number) >= 100 &&
    (value.http_status as number) <= 599;
  if (value.status === "ready" && validHttpStatus) {
    return { status: "ready", httpStatus: value.http_status as number };
  }
  if (value.status !== "failed") return null;
  if (value.reason === "http" && validHttpStatus) {
    return { status: "failed", reason: "http", httpStatus: value.http_status as number };
  }
  if (["dns", "tls", "timeout", "network"].includes(String(value.reason))) {
    return {
      status: "failed",
      reason: value.reason as "dns" | "tls" | "timeout" | "network",
    };
  }
  return null;
}

/**
 * Parse untrusted agent JSON into the internal endpoint observation.
 *
 * Returning a newly constructed value is intentional: provider messages,
 * response bodies, and unknown future fields are discarded at the boundary
 * rather than copied into heartbeat storage.
 */
export function parseEndpointObservationWire(value: unknown): EndpointObservation | null {
  if (!isRecord(value) || !isRecord(value.configuration)) return null;
  const rawWeb = value.configuration.web_origin;
  const rawApi = value.configuration.api_origin;
  const webOrigin =
    typeof rawWeb === "string" && rawWeb.length <= 2048 ? parseEndpointOrigin(rawWeb) : null;
  const apiOrigin =
    typeof rawApi === "string" && rawApi.length <= 2048 ? parseEndpointOrigin(rawApi) : null;
  const checkedAt =
    typeof value.checked_at === "string" && value.checked_at.length <= 64 ? value.checked_at : "";
  const web = parseEndpointProbeWire(value.web);
  const api = parseEndpointProbeWire(value.api);
  if (
    !webOrigin ||
    !apiOrigin ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(checkedAt) ||
    Number.isNaN(Date.parse(checkedAt)) ||
    !web ||
    !api
  ) {
    return null;
  }
  return {
    configuration: { webOrigin, apiOrigin },
    checkedAt,
    web,
    api,
  };
}

/** Parse and reconstruct the bounded, non-secret Kubernetes Service evidence. */
export function parseInternalEndpointObservationWire(
  value: unknown,
): InternalEndpointObservation | null {
  if (!isRecord(value)) return null;
  const checkedAt =
    typeof value.checked_at === "string" && value.checked_at.length <= 64 ? value.checked_at : "";
  const namespace =
    typeof value.namespace === "string" && KUBERNETES_DNS_LABEL.test(value.namespace)
      ? value.namespace
      : "";
  if (
    !namespace ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(checkedAt) ||
    Number.isNaN(Date.parse(checkedAt))
  ) {
    return null;
  }
  if (value.status === "unavailable") {
    return { status: "unavailable", checkedAt, namespace };
  }
  if (value.status !== "observed" || !Array.isArray(value.services)) return null;
  if (value.services.length > INTERNAL_SERVICE_COMPONENTS.length) return null;

  const services: InternalServiceEndpoint[] = [];
  const components = new Set<InternalServiceComponent>();
  let servicePrefix: string | null = null;
  for (const raw of value.services) {
    if (!isRecord(raw)) return null;
    const component = raw.component;
    const serviceName = raw.service_name;
    const port = raw.port;
    const candidatePrefix =
      typeof serviceName === "string" &&
      INTERNAL_SERVICE_COMPONENTS.includes(component as InternalServiceComponent)
        ? internalServiceNamePrefix(serviceName, component as InternalServiceComponent)
        : null;
    if (
      !INTERNAL_SERVICE_COMPONENTS.includes(component as InternalServiceComponent) ||
      components.has(component as InternalServiceComponent) ||
      typeof serviceName !== "string" ||
      candidatePrefix === null ||
      (servicePrefix !== null && servicePrefix !== candidatePrefix) ||
      !Number.isInteger(port) ||
      (port as number) < 1 ||
      (port as number) > 65_535 ||
      raw.url !== internalServiceUrl({ namespace, serviceName, port: port as number })
    ) {
      return null;
    }
    servicePrefix = candidatePrefix;
    components.add(component as InternalServiceComponent);
    services.push({
      component: component as InternalServiceComponent,
      serviceName,
      port: port as number,
    });
  }
  services.sort(
    (left, right) =>
      INTERNAL_SERVICE_COMPONENTS.indexOf(left.component) -
      INTERNAL_SERVICE_COMPONENTS.indexOf(right.component),
  );
  return { status: "observed", checkedAt, namespace, services };
}

export type OciImageReference = {
  registry: string;
  repository: string;
  tag: string | null;
  digest: string | null;
};

const SHA256_DIGEST = /^sha256:[0-9a-f]{64}$/;
const REGISTRY_HOST =
  /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)(?:\.(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?))*(?::[0-9]{1,5})?$/;
const REPOSITORY_PATH = /^[a-z0-9]+(?:[._-][a-z0-9]+)*(?:\/[a-z0-9]+(?:[._-][a-z0-9]+)*)*$/;
const OCI_TAG = /^[A-Za-z0-9_][A-Za-z0-9_.-]{0,127}$/;

/**
 * Parse the fully-qualified image references accepted at the control-plane
 * boundary.
 *
 * This deliberately supports the two forms Quovy emits, not every historical
 * Docker shorthand:
 *
 *   registry.example/quovy-api:release-tag
 *   registry.example/quovy-api@sha256:<64 lowercase hex characters>
 *
 * Requiring a registry and repository keeps the environment-owned registry
 * rewrite explicit. A digest is validated strictly because accepting a value
 * that only LOOKS content-addressed would weaken the customer scan gate.
 */
export function parseOciImageReference(reference: string): OciImageReference | null {
  const slash = reference.indexOf("/");
  if (slash <= 0 || slash === reference.length - 1) return null;

  const registry = reference.slice(0, slash);
  const remainder = reference.slice(slash + 1);
  if (!REGISTRY_HOST.test(registry) || /\s/.test(reference)) return null;

  const at = remainder.lastIndexOf("@");
  if (at >= 0) {
    const repository = remainder.slice(0, at);
    const digest = remainder.slice(at + 1);
    if (!REPOSITORY_PATH.test(repository) || !SHA256_DIGEST.test(digest)) return null;
    return { registry, repository, tag: null, digest };
  }

  const colon = remainder.lastIndexOf(":");
  if (colon <= 0 || colon === remainder.length - 1) return null;
  const repository = remainder.slice(0, colon);
  const tag = remainder.slice(colon + 1);
  if (!REPOSITORY_PATH.test(repository) || !OCI_TAG.test(tag)) return null;
  return { registry, repository, tag, digest: null };
}

/**
 * Identity of a mirrored digest reference, independent of registry and path
 * prefix. Service name remains the map key at the call site, so the final
 * repository segment plus digest is enough to recognise the exact OCI bytes
 * after `quovy-api` moves from Quovy ECR to `customer.azurecr.io/quovy/`.
 *
 * Tag references intentionally return null: tags are mutable and two
 * registries using the same tag do not prove they contain the same artifact.
 */
export function mirroredDigestIdentity(reference: string): string | null {
  const parsed = parseOciImageReference(reference);
  if (!parsed?.digest) return null;
  const repositoryName = parsed.repository.split("/").at(-1);
  return repositoryName ? `${repositoryName}@${parsed.digest}` : null;
}

/**
 * A Kubernetes release must name every chart-owned workload and nothing else.
 *
 * Helm values have defaults, so silently accepting a missing image would mix
 * generations: the omitted workload would keep whatever repository/tag the
 * environment values file carries. Unknown names are rejected too; otherwise
 * a producer typo looks accepted while changing no rendered workload.
 */
export function validateKubernetesReleaseImages(images: unknown): KubernetesReleaseImageContract {
  if (!images || typeof images !== "object" || Array.isArray(images)) {
    return {
      ok: false,
      missing: [...KUBERNETES_RELEASE_IMAGE_NAMES],
      unexpected: [],
      invalid: [],
      detail:
        "Kubernetes release images must be an object naming exactly " +
        KUBERNETES_RELEASE_IMAGE_NAMES.join(", "),
    };
  }

  const supplied = new Set(Object.keys(images));
  const required = new Set<string>(KUBERNETES_RELEASE_IMAGE_NAMES);
  const missing = KUBERNETES_RELEASE_IMAGE_NAMES.filter((name) => !supplied.has(name));
  const unexpected = [...supplied].filter((name) => !required.has(name)).sort();
  const invalid = Object.entries(images)
    .filter(([, reference]) => typeof reference !== "string" || reference.length === 0)
    .map(([name]) => name)
    .sort();

  if (missing.length === 0 && unexpected.length === 0 && invalid.length === 0) {
    return { ok: true };
  }

  const problems = [
    ...(missing.length > 0 ? [`missing: ${missing.join(", ")}`] : []),
    ...(unexpected.length > 0 ? [`unexpected: ${unexpected.join(", ")}`] : []),
    ...(invalid.length > 0 ? [`invalid references: ${invalid.join(", ")}`] : []),
  ];
  return {
    ok: false,
    missing,
    unexpected,
    invalid,
    detail:
      `Kubernetes releases must name exactly ${KUBERNETES_RELEASE_IMAGE_NAMES.join(", ")} ` +
      `(${problems.join("; ")})`,
  };
}
